/** Presentation-only shapes. The API adapter supplies server-aggregated, permission-scoped values. */
export interface StatsMetrics {
  energyMwh: number | null;
  knownEnergyMwh: number;
  powerMwh: number | null;
  gasMwh: number | null;
  buyMwh: number | null;
  sellMwh: number | null;
  powerBuyMwh: number | null;
  gasBuyMwh: number | null;
  executions: number;
  cleared: number;
  bilateral: number;
  pnlEur: number | null;
  pnlReason: string | null;
  mtmReason: string | null;
  costsReason: string | null;
  missingVolumeCount: number;
  costsEur: number | null;
  mtmEur: number | null;
}
export interface StatsMonth {
  month: string;
  powerMwh: number | null;
  gasMwh: number | null;
  executions: number;
  powerExecutions: number;
  gasExecutions: number;
  pnlEur: number | null;
}
export interface StatsMonthlyPerformance { month: string; pnlEur: number | null }
export interface StatsGroup { id: string; label: string; metrics: StatsMetrics; monthlyPerformance: StatsMonthlyPerformance[]; masked?: boolean }
export interface StatsAllocation { commodity: "power" | "gas"; bookId: string; traderId: string; mwh: number }
export interface StatsCross { bookId: string; traderId: string; metrics: StatsMetrics; monthlyPerformance: StatsMonthlyPerformance[] }
export interface StatsFocus { book?: string; trader?: string; commodity?: "power" | "gas"; month?: string }
export type StatsMeasure = "energyMwh" | "powerMwh" | "gasMwh" | "executions" | "pnlEur" | "costsEur" | "mtmEur";

export const integer = (value: number) => new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value);
export const decimal = (value: number, digits = 1) => new Intl.NumberFormat("en-GB", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
export function compact(value: number, digits = 1): string {
  const abs = Math.abs(value);
  return abs >= 1_000_000 ? `${decimal(value / 1_000_000, digits)}m` : abs >= 1_000 ? `${decimal(value / 1_000, digits)}k` : integer(value);
}
export function money(value: number | null, short = false, signed = true): string {
  if (value === null) return "—";
  const sign = value < 0 ? "−" : value > 0 && signed ? "+" : "";
  return `${sign}€${short ? compact(Math.abs(value), Math.abs(value) >= 1_000_000 ? 2 : 1) : decimal(Math.abs(value), 0)}`;
}
export function metricText(value: number | null, key: StatsMeasure): string {
  if (value === null) return "—";
  if (key === "executions") return integer(value);
  if (["energyMwh", "powerMwh", "gasMwh"].includes(key)) return `${decimal(value / 1_000)} GWh`;
  return money(value, false, key !== "costsEur");
}
export function monthName(month: string, year = false): string {
  return new Intl.DateTimeFormat("en-GB", { month: "short", ...(year ? { year: "numeric" as const } : {}), timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
}
export function dateName(day: string): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${day}T12:00:00Z`));
}
export function initials(label: string): string { return label.split(/\s+/).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase(); }
export function sortGroups(groups: readonly StatsGroup[], key: StatsMeasure): StatsGroup[] {
  return [...groups].sort((a, b) => {
    const av = a.metrics[key], bv = b.metrics[key];
    if (av === null) return bv === null ? a.label.localeCompare(b.label) : 1;
    if (bv === null) return -1;
    return bv - av || a.label.localeCompare(b.label);
  });
}
export const measureLabels: Record<StatsMeasure, string> = {
  energyMwh: "Total volume", powerMwh: "Power volume", gasMwh: "Gas volume", executions: "Executions",
  pnlEur: "Recognized P&L", costsEur: "Trading costs", mtmEur: "Open MTM",
};

export type StatsLens = "activity" | "performance";
export function lensForMeasure(measure: StatsMeasure): StatsLens {
  return measure === "pnlEur" || measure === "mtmEur" || measure === "costsEur" ? "performance" : "activity";
}
export function defaultMeasureForLens(lens: StatsLens): StatsMeasure { return lens === "performance" ? "pnlEur" : "energyMwh"; }
/** Each row uses the same maximum, so bar length compares size as well as mix. */
export function volumeMeterWidths(metrics: StatsMetrics, maxVolume: number) {
  if (!(maxVolume > 0)) return { power: 0, gas: 0, unknown: 0 };
  const power = metrics.powerMwh ?? 0, gas = metrics.gasMwh ?? 0;
  const unknown = Math.max(0, metrics.knownEnergyMwh - power - gas);
  const scale = 100 / Math.max(maxVolume, metrics.knownEnergyMwh);
  return { power: power * scale, gas: gas * scale, unknown: unknown * scale };
}
export function performanceSparkBars(months: readonly StatsMonthlyPerformance[]) {
  const known = months.flatMap(month => month.pnlEur === null ? [] : [month.pnlEur]);
  const min = Math.min(0, ...known), max = Math.max(0, ...known), range = max - min || 1;
  const y = (value: number) => 23 - (value - min) / range * 20;
  const slot = 64 / Math.max(1, months.length), width = Math.max(.35, Math.min(4, slot * .65));
  return { zeroY: y(0), bars: months.map((month, index) => ({ ...month, x: slot * index + (slot - width) / 2, width, y: month.pnlEur === null ? null : Math.min(y(0), y(month.pnlEur)), height: month.pnlEur === null ? null : Math.max(.5, Math.abs(y(0) - y(month.pnlEur))) })) };
}

export type StatsDetailSelection = { dimension: "book" | "trader"; id: string } | { dimension: "cross"; bookId: string; traderId: string };
export interface StatsDetail {
  label: string;
  kind: "book" | "trader";
  masked: boolean;
  metrics: StatsMetrics;
  monthlyPerformance: StatsMonthlyPerformance[];
  focus: StatsFocus;
}
export function statsDetail(selection: StatsDetailSelection | null, books: readonly StatsGroup[], traders: readonly StatsGroup[], cross: readonly StatsCross[]): StatsDetail | null {
  if (!selection) return null;
  if (selection.dimension === "cross") {
    const book = books.find(group => group.id === selection.bookId), trader = traders.find(group => group.id === selection.traderId);
    const cell = cross.find(group => group.bookId === selection.bookId && group.traderId === selection.traderId);
    if (!book || !trader || !cell) return null;
    return { label: `${book.label} · ${trader.label}`, kind: "trader", masked: !!trader.masked, metrics: cell.metrics, monthlyPerformance: cell.monthlyPerformance, focus: { book: book.id, trader: trader.id } };
  }
  const group = (selection.dimension === "book" ? books : traders).find(group => group.id === selection.id);
  if (!group) return null;
  return { label: group.label, kind: selection.dimension, masked: !!group.masked, metrics: group.metrics, monthlyPerformance: group.monthlyPerformance, focus: { [selection.dimension]: group.id } };
}
